import { ref, watch, computed } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { formatCoordinate, formatNumber } from '@/utils/math'
import { PoiInterface as navVisPoi } from '@navvis/ivion'
import { PoiProperties as zdbPoi } from '@/interfaces/poi.interface'
import { ivApi } from '@/components/indoorviewer/IndoorviewerView.vue'

export default function useReportPoi() {
  const store = useStore()
  const { t } = useI18n()
  const getPoiProduct = (poiId) => store.dispatch('GET_POI_PRODUCT', poiId)
  const isLoading = ref(false)
  const selectedPoi = computed<navVisPoi | zdbPoi>(() => store.state.selectedPoi)
  const selectedPoiProduct = computed(() => store.state.selectedPoiProduct)
  const selectedBuilding = computed(() => store.state.mapview.selectedBuilding)
  const selectedLevelNumber = computed(() => store.getters.selectedLevelNumber)
  const selectedRealEstate = computed(() => store.state.mapview.selectedRealEstate)
  const isPoiFromBackend = computed(
    () =>
      // @ts-expect-error navVisPoi type does not have matching_title, but it has it during runtime
      !(selectedPoi.value as navVisPoi)?.matching_title && !(selectedPoi.value as navVisPoi)?.pov
  )
  const roomsForSelectedLevel = computed(() => store.state.roomsForSelectedLevel)
  const outdoorPoisForSelectedRealEstate = computed(
    () => store.state.mapview.outdoorPoisForSelectedRealEstate
  )

  const title = computed(() => {
    if (!isPoiFromBackend.value) return (selectedPoi.value as navVisPoi)?.title ?? ''
    if (selectedPoi.value as zdbPoi) {
      return (selectedPoi.value as zdbPoi).name
        ? (selectedPoi.value as zdbPoi).name
        : (selectedPoi.value as zdbPoi).title
    }
    return ''
  })
  const descriptionList = computed<string[]>(() => {
    if (!isPoiFromBackend.value) {
      const description = (selectedPoi.value as navVisPoi)?.description
      if (!description) return []
      if (description.startsWith('<')) return getPoiDescription(description)
      else return [description]
    }
    if (!selectedPoi.value?.description) return []
    if (selectedPoi.value.description.startsWith('<'))
      return getPoiDescription(selectedPoi.value.description)
    else return [selectedPoi.value.description]
  })

  const coordinates = computed(() => {
    if (!selectedPoi.value) return null
    if (isPoiFromBackend.value && (selectedPoi.value as zdbPoi).latitude) {
      return {
        latitude: (selectedPoi.value as zdbPoi).latitude,
        longitude: (selectedPoi.value as zdbPoi).longitude,
        height: (selectedPoi.value as zdbPoi).elevation
      }
    } else {
      // Ivion doesn't include latitude as poi property, and location comes in local coordinates
      const ivionPosition = ivApi.transform.service.localToGlobal.transform(
        (selectedPoi.value as navVisPoi).location.clone()
      )
      ivionPosition.x = Math.round(ivionPosition.x * 1e9) / 1e9
      ivionPosition.y = Math.round(ivionPosition.y * 1e9) / 1e9
      ivionPosition.z = Math.round(ivionPosition.z * 1e2) / 1e2
      return {
        latitude: ivionPosition.y,
        longitude: ivionPosition.x,
        height: ivionPosition.z
      }
    }
  })

  const roomNameForSelectedPoi = computed<string>(() => {
    if (!isPoiFromBackend.value) {
      // @ts-expect-error navVisPoi type does not have siteModelEntity, but it has it during runtime
      return (selectedPoi.value as navVisPoi)?.siteModelEntity?.title ?? ''
    }
    const roomForPoi = roomsForSelectedLevel.value?.data.features.find(
      (room) => room.id === (selectedPoi.value as zdbPoi)?.coreId
    )
    return roomForPoi?.properties?.name ?? ''
  })
  const metaInfo = computed<{ title: string; type?: string; value: string }[]>(() => {
    const list: { title: string; type?: string; value: string }[] = selectedLevelNumber.value
      ? [
          {
            title: t('general__text__building'),
            value: selectedBuilding.value?.name ?? ''
          },
          {
            title: t('general__text__level'),
            value: selectedLevelNumber.value ?? ''
          },
          {
            title: t('general__text__room'),
            value: roomNameForSelectedPoi.value
          }
        ]
      : [
          {
            title: t('general__title__realestate'),
            value: selectedRealEstate.value?.name ?? ''
          }
        ]
    if (coordinates.value) {
      if (coordinates.value.latitude)
        list.push({
          title: t('general__text__latitude'),
          type: 'latitude',
          value: formatCoordinate(coordinates.value.latitude)
        })
      if (coordinates.value.longitude)
        list.push({
          title: t('general__text__longitude'),
          type: 'longitude',
          value: formatCoordinate(coordinates.value.longitude)
        })
      if (coordinates.value.height)
        list.push({
          title: t('general__text__height-in-RH'),
          type: 'heightRH',
          value: formatNumber(coordinates.value.height, 2)
        })
    }
    if (
      isPoiFromBackend.value &&
      (selectedPoi.value as zdbPoi)?.estimatedHeight !== null &&
      (selectedPoi.value as zdbPoi)?.estimatedHeight !== undefined
    ) {
      const outdoorPoi = outdoorPoisForSelectedRealEstate.value?.find(
        (poi) => poi.properties.guid === (selectedPoi.value as zdbPoi)?.guid
      )
      list.push({
        title: outdoorPoi
          ? t('general__text__estimated-height-from-ground')
          : t('general__text__estimated-height-from-floor'),
        type: 'heightFrom',
        value: formatNumber((selectedPoi.value as zdbPoi).estimatedHeight, 2)
      })
    }
    return list
  })

  // remove function when poi's are updated to not have html tags
  const getPoiDescription = (description: string) => {
    const descriptionWithoutImageTags = description
      .replace(/<img.*<\/img>/g, '')
      .replace(/<a.*<\/a>/g, '')
      .replace(/&nbsp;/g, ' ') // Removing image and anchor tag due to BIMobject issues (old connection for validated pois, for example in AWL)
    const descriptions = descriptionWithoutImageTags.replace(/<[^>]*>?/gm, ',').split(',')
    return descriptions.filter((descriptionItem) => {
      if (descriptionItem.length) return descriptionItem
    })
  }

  const onSelectedPoiChange = async () => {
    if (!selectedPoi.value) return
    isLoading.value = true
    await getPoiProduct((selectedPoi.value as zdbPoi).productId)
    isLoading.value = false
  }

  watch(selectedPoi, onSelectedPoiChange, { immediate: true })

  return {
    isLoading,
    isPoiFromBackend,
    title,
    selectedPoiProduct,
    descriptionList,
    metaInfo,
    selectedPoi
  }
}
